import { create } from "zustand";
import toast from "react-hot-toast";
import type { OrderItemCreate } from "@/types";

export interface CartItem extends OrderItemCreate {
  good_name: string;
  selling_price: number;
  /** Available stock (count - booking_count). Used to cap quantity. */
  max_quantity: number;
}

interface CartState {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (goodNumber: number) => void;
  updateQuantity: (goodNumber: number, quantity: number) => void;
  /** Update max_quantity for an item (e.g. when fresh stock data arrives). */
  updateStock: (goodNumber: number, maxQuantity: number) => void;
  clear: () => void;
  total: () => number;
  count: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],

  addItem: (item) => {
    set((state) => {
      const existing = state.items.find(
        (i) => i.good_number === item.good_number,
      );
      if (existing) {
        const newQty = existing.quantity + item.quantity;
        const max = item.max_quantity;
        if (newQty > max) {
          toast.error(
            `Максимум ${max} шт. "${item.good_name}" в наличии`,
            { id: `max-qty-${item.good_number}` },
          );
          // Still set to max if not already there
          if (existing.quantity >= max) return state;
          return {
            items: state.items.map((i) =>
              i.good_number === item.good_number
                ? { ...i, quantity: max, max_quantity: max }
                : i,
            ),
          };
        }
        return {
          items: state.items.map((i) =>
            i.good_number === item.good_number
              ? { ...i, quantity: newQty, max_quantity: item.max_quantity }
              : i,
          ),
        };
      }
      // New item
      const max = item.max_quantity;
      if (item.quantity > max) {
        toast.error(
          `Максимум ${max} шт. "${item.good_name}" в наличии`,
          { id: `max-qty-${item.good_number}` },
        );
        return { items: [...state.items, { ...item, quantity: max }] };
      }
      return { items: [...state.items, item] };
    });
  },

  removeItem: (goodNumber) => {
    set((state) => ({
      items: state.items.filter((i) => i.good_number !== goodNumber),
    }));
  },

  updateQuantity: (goodNumber, quantity) => {
    if (quantity <= 0) {
      get().removeItem(goodNumber);
      return;
    }
    set((state) => {
      const item = state.items.find((i) => i.good_number === goodNumber);
      if (!item) return state;
      const max = item.max_quantity;
      if (quantity > max) {
        toast.error(
          `Максимум ${max} шт. "${item.good_name}" в наличии`,
          { id: `max-qty-${goodNumber}` },
        );
        // Clamp to max
        if (item.quantity >= max) return state;
        return {
          items: state.items.map((i) =>
            i.good_number === goodNumber ? { ...i, quantity: max } : i,
          ),
        };
      }
      return {
        items: state.items.map((i) =>
          i.good_number === goodNumber ? { ...i, quantity } : i,
        ),
      };
    });
  },

  updateStock: (goodNumber, maxQuantity) => {
    set((state) => ({
      items: state.items.map((i) => {
        if (i.good_number !== goodNumber) return i;
        const clampedQty = Math.min(i.quantity, maxQuantity);
        return { ...i, max_quantity: maxQuantity, quantity: clampedQty };
      }),
    }));
  },

  clear: () => set({ items: [] }),

  total: () =>
    get().items.reduce((sum, i) => sum + i.selling_price * i.quantity, 0),

  count: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
}));
